"use client"

import AvatarDecoration from "@/components/views/avatar-decoration/AvatarDecoration"
import { DEFAULT_IMAGES } from "@/lib/constants"
import { useSite } from "@/lib/hooks/site"
import { useEffect, useState } from "react"
import S3Image from "@/components/S3Image"
import dynamic from "next/dynamic"
import { Button } from "@/components/ui/button"
import { StarIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { ChatBubbleLeftRightIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import AddReplyModal from "../../new-details/comments/AddReplyModal"
import { formatDistanceToNow } from "date-fns"
import { tr } from "date-fns/locale"
import { useTranslation } from "@/hooks/translationContext"

interface Props {
  comment: Venue.Comment
  level?: number
  handleShowReply?: (parent: ParentComment) => void;
}

const MAX_REPLY_LEVEL = 2

export default function CommentCard(props: Props) {
  const { t } = useTranslation();
  const site = useSite()
  const [imageSrc, setImageSrc] = useState(
    props.comment?.avatar ? `user/${props.comment.userId}/${props.comment?.avatar}` : DEFAULT_IMAGES.WORKER,
  )

  useEffect(() => {
    setImageSrc(props.comment?.avatar ? `user/${props.comment.userId}/${props.comment?.avatar}` : DEFAULT_IMAGES.WORKER)
  }, [props.comment])

  const isMainComment = (props.level || 0) === 0
  const hasReplies = props.comment?.repliesCount > 0;
  const canReply = (props.level || 0) < MAX_REPLY_LEVEL

  const handleReplyClick = (parentId) => {
    if (!props.handleShowReply) return;

    const data = {
      comment: props.comment.text,
      fullName: props.comment?.userName,
      id: parentId,
    } as ParentComment;

    props.handleShowReply(data)
  }

  return (
    <>
      <div
        className={`
        overflow-hidden border border-gray rounded-[24px] w-full
        ${isMainComment ? "bg-white shadow-sm" : "bg-gray-50/50 border-gray-200"}
      `}
      >
        {/* Ana Yorum İçeriği */}
        <div className="flex p-4 gap-3">
          <div className="flex-shrink-0">
            <AvatarDecoration className="relative w-8 h-8 rounded-xl">
              <S3Image
                width={32}
                height={32}
                onError={(e) => {
                  setImageSrc(DEFAULT_IMAGES.WORKER)
                }}
                className="w-8 h-8 object-cover rounded-xl border border-white"
                src={imageSrc}
                alt={t("venue-detail-comment-card-avatar-alt")}
              />
            </AvatarDecoration>
          </div>

          <div className="flex flex-col gap-3 flex-1 min-w-0">
            {/* Kullanıcı Bilgisi ve Rating */}
            <div className="flex justify-between items-start">
              <div className="flex flex-col gap-1">
                <span className="text-assistant text-sm font-bold">{props.comment.userName || t("common-not-found")}</span>
                {props.comment.rating > 0 && (
                  <div className="flex items-center gap-1">
                    {Array.from({ length: props.comment.rating }, (_, i) => i + 1).map((star) => (
                      <StarIcon key={star} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                )}
              </div>
              {props.comment.approvedAt && (
                <div className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(props.comment.approvedAt), { 
                    addSuffix: true, 
                    locale: tr 
                  })}
                </div>
              )}
            </div>

            {/* Yorum Metni */}
            <div className="w-full">
              <p className="text-left text-sm text-primary font-medium leading-relaxed">
                {props.comment.text}
              </p>
            </div>

            {/* Aksiyon Butonları */}
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-3">
                {canReply && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleReplyClick(props.comment.id)}
                    className="w-auto flex-row rounded-2xl font-bold justify-center items-center bg-white border border-assistant text-assistant hover:bg-slate-100 disabled:!p-current disabled:!scale-100 py-3 px-4 m-0 transition-all duration-400 ease-in-out active:scale-[1.015] active:py-4 active:my-0.5 active:px-3 active:mx-1 !h-6 flex gap-2 !p-2 text-sm disabled:opacity-30 disabled:cursor-not-allowed !transition-none"
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    Yanıtla
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reply'ler - Ana kart içerisinde */}
        {hasReplies && (
          <div className="border-t border-gray-100">
            <div className="p-3 space-y-3">
              <div className="text-xs text-gray-500 font-medium px-1">Yanıtlar ({props.comment?.repliesCount})</div>
              <div className="space-y-3">
                {props.comment.replies.map((reply, index) => (
                  <div key={reply.id} className="relative">
                    {/* Reply için sol çizgi */}
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200"></div>

                    <div className="pl-8">
                      <CommentCard handleShowReply={() => handleReplyClick(reply.id)} comment={reply} level={(props.level || 0) + 1} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}